// Servicio de Categorías y Subcategorías
// Maneja las operaciones CRUD para categorías y subcategorías en Firestore

import { 
  getCollection, 
  getDocument, 
  createDocument, 
  updateDocument, 
  deleteDocument 
} from './firestore.service'

const COLLECTIONS = {
  CATEGORIES: 'categories',
  SUBCATEGORIES: 'subcategories'
}

/**
 * ============================================
 * SERVICIO DE CATEGORÍAS
 * ============================================
 */

/**
 * Obtener todas las categorías activas
 * @param {boolean} includeInactive - Si incluir categorías inactivas
 * @returns {Promise<Array>} Lista de categorías
 */
export const getCategories = async (includeInactive = false) => {
  try {
    // Obtener todas las categorías sin filtros para evitar índices compuestos
    // Filtrar y ordenar en el cliente
    const allCategories = await getCollection(COLLECTIONS.CATEGORIES, [], 'name', 'asc')
    
    // Filtrar por activas si es necesario
    let filtered = allCategories
    if (!includeInactive) {
      filtered = allCategories.filter(cat => cat.active !== false)
    }
    
    // Ordenar por nombre
    return filtered.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase()
      const nameB = (b.name || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
  } catch (error) {
    console.error('Error obteniendo categorías:', error)
    throw error
  }
}

/**
 * Obtener una categoría por ID
 * @param {string} id - ID de la categoría
 * @returns {Promise<Object>} Categoría
 */
export const getCategory = async (id) => {
  try {
    return await getDocument(COLLECTIONS.CATEGORIES, id)
  } catch (error) {
    console.error('Error obteniendo categoría:', error)
    throw error
  }
}

/**
 * Crear una nueva categoría
 * @param {Object} categoryData - Datos de la categoría
 * @returns {Promise<string>} ID de la categoría creada
 */
export const createCategory = async (categoryData) => {
  try {
    const data = {
      name: categoryData.name.trim(),
      slug: categoryData.slug || categoryData.name.toLowerCase().replace(/\s+/g, '-'),
      description: categoryData.description?.trim() || '',
      active: categoryData.active !== false,
      profitPercentage: categoryData.profitPercentage !== undefined && categoryData.profitPercentage !== null 
        ? parseFloat(categoryData.profitPercentage) 
        : null,
      preparationCosts: categoryData.preparationCosts || [], // Costos de preparación
      imageUrl: categoryData.imageUrl || null, // URL de la imagen representativa
      createdAt: new Date(),
      updatedAt: new Date()
    }
    return await createDocument(COLLECTIONS.CATEGORIES, data)
  } catch (error) {
    console.error('Error creando categoría:', error)
    throw error
  }
}

/**
 * Actualizar una categoría
 * @param {string} id - ID de la categoría
 * @param {Object} categoryData - Datos actualizados
 * @returns {Promise<void>}
 */
export const updateCategory = async (id, categoryData) => {
  try {
    const data = {
      ...categoryData,
      name: categoryData.name?.trim(),
      slug: categoryData.slug || categoryData.name?.toLowerCase().replace(/\s+/g, '-'),
      description: categoryData.description?.trim() || '',
      profitPercentage: categoryData.profitPercentage !== undefined && categoryData.profitPercentage !== null 
        ? parseFloat(categoryData.profitPercentage) 
        : null,
      preparationCosts: categoryData.preparationCosts || [], // Costos de preparación
      imageUrl: categoryData.imageUrl !== undefined ? categoryData.imageUrl : null, // URL de la imagen representativa
      updatedAt: new Date()
    }
    return await updateDocument(COLLECTIONS.CATEGORIES, id, data)
  } catch (error) {
    console.error('Error actualizando categoría:', error)
    throw error
  }
}

/**
 * Eliminar una categoría
 * @param {string} id - ID de la categoría
 * @returns {Promise<void>}
 */
export const deleteCategory = async (id) => {
  try {
    return await deleteDocument(COLLECTIONS.CATEGORIES, id)
  } catch (error) {
    console.error('Error eliminando categoría:', error)
    throw error
  }
}

/**
 * ============================================
 * SERVICIO DE SUBCATEGORÍAS
 * ============================================
 */

/**
 * Obtener todas las subcategorías
 * @param {string} categoryId - Filtrar por categoría (opcional)
 * @param {boolean} includeInactive - Si incluir subcategorías inactivas
 * @returns {Promise<Array>} Lista de subcategorías
 */
export const getSubcategories = async (categoryId = null, includeInactive = false) => {
  try {
    // Obtener todas las subcategorías sin filtros para evitar índices compuestos
    // Filtrar y ordenar en el cliente
    let allSubcategories = await getCollection(COLLECTIONS.SUBCATEGORIES, [], 'name', 'asc')
    
    // Filtrar por categoría si se especifica
    if (categoryId) {
      allSubcategories = allSubcategories.filter(sub => sub.categoryId === categoryId)
    }
    
    // Filtrar por activas si es necesario
    let filtered = allSubcategories
    if (!includeInactive) {
      filtered = allSubcategories.filter(sub => sub.active !== false)
    }
    
    // Ordenar por nombre
    return filtered.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase()
      const nameB = (b.name || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
  } catch (error) {
    console.error('Error obteniendo subcategorías:', error)
    throw error
  }
}

/**
 * Obtener una subcategoría por ID
 * @param {string} id - ID de la subcategoría
 * @returns {Promise<Object>} Subcategoría
 */
export const getSubcategory = async (id) => {
  try {
    return await getDocument(COLLECTIONS.SUBCATEGORIES, id)
  } catch (error) {
    console.error('Error obteniendo subcategoría:', error)
    throw error
  }
}

/**
 * Crear una nueva subcategoría
 * @param {Object} subcategoryData - Datos de la subcategoría
 * @returns {Promise<string>} ID de la subcategoría creada
 */
export const createSubcategory = async (subcategoryData) => {
  try {
    const data = {
      name: subcategoryData.name.trim(),
      slug: subcategoryData.slug || subcategoryData.name.toLowerCase().replace(/\s+/g, '-'),
      categoryId: subcategoryData.categoryId,
      description: subcategoryData.description?.trim() || '',
      active: subcategoryData.active !== false,
      createdAt: new Date(),
      updatedAt: new Date()
    }
    return await createDocument(COLLECTIONS.SUBCATEGORIES, data)
  } catch (error) {
    console.error('Error creando subcategoría:', error)
    throw error
  }
}

/**
 * Actualizar una subcategoría
 * @param {string} id - ID de la subcategoría
 * @param {Object} subcategoryData - Datos actualizados
 * @returns {Promise<void>}
 */
export const updateSubcategory = async (id, subcategoryData) => {
  try {
    const data = {
      ...subcategoryData,
      name: subcategoryData.name?.trim(),
      slug: subcategoryData.slug || subcategoryData.name?.toLowerCase().replace(/\s+/g, '-'),
      description: subcategoryData.description?.trim() || '',
      updatedAt: new Date()
    }
    return await updateDocument(COLLECTIONS.SUBCATEGORIES, id, data)
  } catch (error) {
    console.error('Error actualizando subcategoría:', error)
    throw error
  }
}

/**
 * Eliminar una subcategoría
 * @param {string} id - ID de la subcategoría
 * @returns {Promise<void>}
 */
export const deleteSubcategory = async (id) => {
  try {
    return await deleteDocument(COLLECTIONS.SUBCATEGORIES, id)
  } catch (error) {
    console.error('Error eliminando subcategoría:', error)
    throw error
  }
}

export default {
  // Categorías
  getCategories,
  getCategory,
  createCategory,
  updateCategory,
  deleteCategory,
  // Subcategorías
  getSubcategories,
  getSubcategory,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory
}

